import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiMail, FiCheck, FiAlertTriangle } from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import authService from "../../services/authService";
import { handleNetworkError } from "../../utils/errorUtils";
import {
  AuthLayout,
  AuthButton,
  VerificationCodeInput,
} from "./components";
import styles from "./Auth.module.css";

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, setIsAuthenticated } = useUserContext();
  
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  // Get redirect destination
  const from = location.state?.from || "/onboarding";

  // Get email from state or user context
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    } else if (user?.email) {
      setEmail(user.email);
    } else {
      navigate("/signin", { replace: true });
    }
  }, [location.state, user, navigate]);

  // Handle cooldown timer
  useEffect(() => {
    let intervalId = null;
    if (cooldown > 0) {
      intervalId = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [cooldown]);

  // Handle code change
  const handleCodeChange = (newCode) => {
    setVerificationCode(newCode);
    if (error) setError("");

    // Auto-submit when code is complete
    const isComplete = newCode.every((digit) => digit !== "");
    if (isComplete) {
      handleSubmit(newCode);
    }
  };

  // Submit verification code
  const handleSubmit = async (codeArray = verificationCode) => {
    const code = codeArray.join("");

    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setError("Please enter a valid 6-digit verification code");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const response = await authService.verifyEmail({
        email,
        verificationCode: code,
      });

      if (response && response.token) {
        setUser(response);
        setIsAuthenticated(true);
      }

      setShowSuccess(true);

      setTimeout(() => {
        navigate("/onboarding", { replace: true });
      }, 2000);
    } catch (err) {
      const processedError = handleNetworkError(err);
      setError(processedError.message);
      setIsLoading(false);
    }
  };

  // Resend verification code
  const handleResend = async () => {
    try {
      setResendLoading(true);
      setError("");

      const response = await authService.resendVerificationCode({ email });
      setSuccess(response.message || "Verification code sent!");
      setCooldown(60);

      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      const processedError = handleNetworkError(err);
      setError(processedError.message);
    } finally {
      setResendLoading(false);
    }
  };

  const isCodeComplete = verificationCode.every((digit) => digit !== "");

  // Success state
  if (showSuccess) {
    return (
      <AuthLayout>
        <motion.div
          className={styles['au-success']}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            className={styles['au-success__icon']}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <FiCheck size={32} />
          </motion.div>
          <h2 className={styles['au-success__title']}>Email Verified!</h2>
          <p className={styles['au-success__text']}>Redirecting to onboarding...</p>
        </motion.div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      icon={<FiMail size={28} />}
      title="Verify Your Email"
      subtitle="We've sent a 6-digit verification code to:"
    >
      {/* Email Display */}
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <span className={styles['au-email-display']}>{email}</span>
      </div>

      {/* Alerts */}
      <AnimatePresence>
        {error && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--error']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiAlertTriangle className={styles['au-alert__icon']} />
            <span>{error}</span>
          </motion.div>
        )}

        {success && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--success']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiCheck className={styles['au-alert__icon']} />
            <span>{success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Verification Code Input */}
      <VerificationCodeInput
        value={verificationCode}
        onChange={handleCodeChange}
        disabled={isLoading}
      />

      {/* Submit Button */}
      <AuthButton
        type="button"
        variant="primary"
        fullWidth
        loading={isLoading}
        disabled={!isCodeComplete}
        onClick={() => handleSubmit()}
      >
        {isLoading ? "Verifying..." : "Verify Email"}
      </AuthButton>

      {/* Resend Section */}
      <div className={styles['au-resend']}>
        <span>Didn't receive the code? </span>
        <button
          type="button"
          className={styles['au-resend__btn']}
          onClick={handleResend}
          disabled={resendLoading || cooldown > 0}
        >
          {resendLoading
            ? "Sending..."
            : cooldown > 0
            ? `Resend in ${cooldown}s`
            : "Resend Code"}
        </button>
      </div>
    </AuthLayout>
  );
};

export default EmailVerification;
